/* eslint-disable no-underscore-dangle */
import * as React from 'react';
import {
  DataGridPremium,
  useGridApiRef,
  GridActionsCellItem,
  gridClasses,
  GridActionsCell,
  useGridApiContext,
} from '@mui/x-data-grid-premium';
import { useDemoData } from '@mui/x-data-grid-generator';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import SaveIcon from '@mui/icons-material/Save';
import { darken } from '@mui/material/styles';

const visibleFields = [
  'id',
  'commodity',
  'traderName',
  'traderEmail',
  'quantity',
  'filledQuantity',
];

const BulkEditingContext = React.createContext(null);

function ActionsCell(props) {
  const context = React.useContext(BulkEditingContext);
  const apiRef = useGridApiContext();
  if (!context) {
    return null;
  }
  const { unsavedChangesRef, setHasUnsavedRows } = context;
  const { id, row } = props;

  return (
    <GridActionsCell {...props}>
      <GridActionsCellItem
        icon={<RestoreIcon />}
        label="Discard changes"
        disabled={unsavedChangesRef.current.unsavedRows[id] === undefined}
        onClick={() => {
          apiRef.current?.updateRows([
            unsavedChangesRef.current.rowsBeforeChange[id],
          ]);
          delete unsavedChangesRef.current.rowsBeforeChange[id];
          delete unsavedChangesRef.current.unsavedRows[id];
          setHasUnsavedRows(
            Object.keys(unsavedChangesRef.current.unsavedRows).length > 0,
          );
        }}
      />
      <GridActionsCellItem
        icon={<DeleteIcon />}
        label="Delete"
        onClick={() => {
          unsavedChangesRef.current.unsavedRows[id] = {
            ...row,
            _action: 'delete',
          };
          if (!unsavedChangesRef.current.rowsBeforeChange[id]) {
            unsavedChangesRef.current.rowsBeforeChange[id] = row;
          }
          setHasUnsavedRows(true);
          apiRef.current?.updateRows([row]); // to trigger row render
        }}
      />
    </GridActionsCell>
  );
}

export default function BulkEditingPremiumNoSnap() {
  const { data } = useDemoData({
    dataSet: 'Commodity',
    rowLength: 100,
    maxColumns: 7,
    editable: true,
    visibleFields,
  });

  const apiRef = useGridApiRef();

  const [hasUnsavedRows, setHasUnsavedRows] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const unsavedChangesRef = React.useRef({
    unsavedRows: {},
    rowsBeforeChange: {},
  });

  const columns = React.useMemo(() => {
    return [
      {
        field: 'actions',
        type: 'actions',
        renderCell: (params) => <ActionsCell {...params} />,
      },
      ...data.columns,
    ];
  }, [data.columns]);

  const processRowUpdate = React.useCallback((newRow, oldRow) => {
    const rowId = newRow.id;

    unsavedChangesRef.current.unsavedRows[rowId] = newRow;
    if (!unsavedChangesRef.current.rowsBeforeChange[rowId]) {
      unsavedChangesRef.current.rowsBeforeChange[rowId] = oldRow;
    }
    setHasUnsavedRows(true);
    return newRow;
  }, []);

  const discardChanges = React.useCallback(() => {
    setHasUnsavedRows(false);
    apiRef.current?.updateRows(
      Object.values(unsavedChangesRef.current.rowsBeforeChange),
    );
    unsavedChangesRef.current = {
      unsavedRows: {},
      rowsBeforeChange: {},
    };
  }, [apiRef]);

  const saveChanges = React.useCallback(async () => {
    try {
      // Persist updates in the database
      setIsSaving(true);
      await new Promise((resolve) => {
        setTimeout(resolve, 1000);
      });

      setIsSaving(false);
      const rowsToDelete = Object.values(
        unsavedChangesRef.current.unsavedRows,
      ).filter((row) => row._action === 'delete');
      if (rowsToDelete.length > 0) {
        apiRef.current?.updateRows(rowsToDelete);
      }

      setHasUnsavedRows(false);
      unsavedChangesRef.current = {
        unsavedRows: {},
        rowsBeforeChange: {},
      };
    } catch (error) {
      setIsSaving(false);
    }
  }, [apiRef]);

  const getRowClassName = React.useCallback(({ id }) => {
    const unsavedRow = unsavedChangesRef.current.unsavedRows[id];
    if (unsavedRow) {
      if (unsavedRow._action === 'delete') {
        return 'row--removed';
      }
      return 'row--edited';
    }
    return '';
  }, []);

  const bulkEditingContextValue = React.useMemo(
    () => ({
      unsavedChangesRef,
      setHasUnsavedRows,
    }),
    [unsavedChangesRef],
  );

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: 8 }}>
        <Button
          disabled={!hasUnsavedRows}
          loading={isSaving}
          onClick={saveChanges}
          startIcon={<SaveIcon />}
          loadingPosition="start"
        >
          <span>Save</span>
        </Button>
        <Button
          disabled={!hasUnsavedRows || isSaving}
          onClick={discardChanges}
          startIcon={<RestoreIcon />}
        >
          Discard all changes
        </Button>
      </div>
      <div style={{ height: 400 }}>
        <BulkEditingContext.Provider value={bulkEditingContextValue}>
          <DataGridPremium
            {...data}
            columns={columns}
            apiRef={apiRef}
            disableRowSelectionOnClick
            cellSelection
            processRowUpdate={processRowUpdate}
            ignoreValueFormatterDuringExport
            sx={{
              [`& .${gridClasses.row}.row--removed`]: {
                backgroundColor: (theme) => {
                  if (theme.palette.mode === 'light') {
                    return 'rgba(255, 170, 170, 0.3)';
                  }
                  return darken('rgba(255, 170, 170, 1)', 0.7);
                },
              },
              [`& .${gridClasses.row}.row--edited`]: {
                backgroundColor: (theme) => {
                  if (theme.palette.mode === 'light') {
                    return 'rgba(255, 254, 176, 0.3)';
                  }
                  return darken('rgba(255, 254, 176, 1)', 0.6);
                },
              },
            }}
            loading={isSaving}
            getRowClassName={getRowClassName}
          />
        </BulkEditingContext.Provider>
      </div>
    </div>
  );
}
